import { useStateQuery } from '../api/state';
import { useHistoryQuery } from '../api/history';
import { BackLink } from '../components/layout/BackLink';
import { FilterableHistoryChart } from '../components/chart/FilterableHistoryChart';
import { HistoryChart, type ChartPoint } from '../components/chart/HistoryChart';
import { RangeSelector } from '../components/chart/RangeSelector';
import { useChartRange, bucketProps } from '../components/chart/useChartRange';
import { deriveActivity, isSlabPumping, isCompressorRunning } from '../domain/wpm-status';

export function HeatPumpPage() {
  const state = useStateQuery();

  const flowRange    = useChartRange();
  const pumpRange    = useChartRange();
  const weatherRange = useChartRange();

  const history      = useHistoryQuery('heat_pump_link', 'heat_pump', flowRange.range);
  const pumpHistory  = useHistoryQuery('heat_pump_link', 'heat_pump', pumpRange.range);
  const weather      = useHistoryQuery('weather', 'site', weatherRange.range);

  if (state.isLoading) return <div className="skeleton" style={{ height: 200 }} />;
  if (state.isError || !state.data) return <div className="banner danger">Failed to load.</div>;
  const hp = state.data.heat_pump;
  const activity = deriveActivity(hp);

  // Each LinkHistory row stores the heat-pump object as a JSON `snapshot`
  // string (see cloud to-history.ts), so the flow temp lives at
  // snapshot.current_flow_temp_c — not as a flat column.
  const flow: ChartPoint[] = (history.data?.rows ?? [])
    .map((r) => {
      let v: number | undefined;
      const snap = r['snapshot'];
      if (typeof snap === 'string') {
        try {
          v = (JSON.parse(snap) as { current_flow_temp_c?: number }).current_flow_temp_c;
        } catch {
          v = undefined;
        }
      }
      return { t: Date.parse(r['at'] as string), v: v as number };
    })
    .filter((p) => Number.isFinite(p.v));

  // WPM-reported outside temp lives in the same snapshot JSON string.
  const outsideHp: ChartPoint[] = (history.data?.rows ?? [])
    .map((r) => {
      let v: number | undefined;
      const snap = r['snapshot'];
      if (typeof snap === 'string') {
        try {
          v = (JSON.parse(snap) as { outside_temp_c?: number }).outside_temp_c;
        } catch {
          v = undefined;
        }
      }
      return { t: Date.parse(r['at'] as string), v: v as number };
    })
    .filter((p) => Number.isFinite(p.v));

  // Pump / compressor history. At raw ranges this is a crisp on/off step line
  // decoded from the operating-status bits in snapshot.fault_bitmap. When the
  // API bucketed the response, the bucketer supplied slab_pump_fraction /
  // compressor_fraction instead — a duty-cycle line, which is the honest
  // reading at that zoom (a bitmap can't be averaged).
  const pumpBucketMs = pumpHistory.data?.bucketMs;
  const pumpBucketed = pumpBucketMs !== undefined;
  const statusSeries = (
    on: (faultBitmap: number) => boolean,
    fractionKey: 'slab_pump_fraction' | 'compressor_fraction',
  ): ChartPoint[] =>
    (pumpHistory.data?.rows ?? [])
      .map((r) => {
        const snap = r['snapshot'];
        if (typeof snap !== 'string') return { t: Date.parse(r['at'] as string), v: NaN };
        let parsed: { fault_bitmap?: number; slab_pump_fraction?: number; compressor_fraction?: number };
        try {
          parsed = JSON.parse(snap) as typeof parsed;
        } catch {
          return { t: Date.parse(r['at'] as string), v: NaN };
        }
        const frac = parsed[fractionKey];
        if (pumpBucketed && typeof frac === 'number') {
          return { t: Date.parse(r['at'] as string), v: frac };
        }
        const fb = parsed.fault_bitmap;
        return { t: Date.parse(r['at'] as string), v: fb === undefined ? NaN : on(fb) ? 1 : 0 };
      })
      .filter((p) => Number.isFinite(p.v));
  const pumpingSeries    = statusSeries(isSlabPumping, 'slab_pump_fraction');
  const compressorSeries = statusSeries(isCompressorRunning, 'compressor_fraction');

  // Weather rows carry the measurements as DIRECT columns (outsideTempC,
  // cloudCoverPct, solarRadiationWm2) — not inside a JSON snapshot string.
  const toSeries = (rows: Array<Record<string, unknown>>, key: string): ChartPoint[] =>
    rows
      .map((r) => ({ t: Date.parse(r['at'] as string), v: r[key] as number }))
      .filter((p) => Number.isFinite(p.v));
  const wxRows = weather.data?.rows ?? [];
  const outsideTemp = toSeries(wxRows, 'outsideTempC');
  const solar = toSeries(wxRows, 'solarRadiationWm2');
  // cloudCoverPct arrives 0–100; the heat strip's colour ramp expects a 0–1
  // fraction, same convention as ChartBars.colorBy.
  const cloud = toSeries(wxRows, 'cloudCoverPct').map((p) => ({ t: p.t, v: p.v / 100 }));

  return (
    <>
      <BackLink />
      <h1>Heat pump</h1>
      <div className="card">
        <div className="group-row">
          <span>Link health</span>
          <span className={`tag ${hp.link_health === 'alive' ? 'open' : 'closed'}`}>{hp.link_health}</span>
        </div>
        <div className="group-row">
          <span>Activity</span>
          <span>
            <span className={`tag ${activity.tone}`}>{activity.label}</span>
            {hp.summer_mode && <span className="tag" style={{ marginLeft: 'var(--sp-2)' }}>summer</span>}
          </span>
        </div>
        <div className="group-row">
          <span>Heating</span>
          <span className={`tag ${activity.pumping ? 'heat' : 'none'}`}>{activity.pumping ? 'pumping' : 'off'}</span>
        </div>
        <div className="group-row">
          <span>Heat pump</span>
          <span className={`tag ${isCompressorRunning(hp.fault_bitmap) ? 'heat' : 'none'}`}>
            {isCompressorRunning(hp.fault_bitmap) ? 'running' : 'idle'}
          </span>
        </div>
        <div className="group-row">
          <span>Operating mode</span>
          <span className="tag">{hp.operating_mode}</span>
        </div>
        <div className="group-row">
          <span>Outside temp</span>
          <span className="tabular">{hp.outside_temp_c.toFixed(1)} °C</span>
        </div>
        <div className="group-row">
          <span>Flow temperature</span>
          <span className="tabular">{hp.current_flow_temp_c.toFixed(1)} °C</span>
        </div>
        <div className="group-row">
          <span>HC1 target</span>
          <span className="tabular">{hp.hc1_setpoint_c.toFixed(1)} °C</span>
        </div>
        <div className="group-row">
          <span>Dew-point alarm</span>
          <span className={`tag ${hp.dewpoint_alarm ? 'warning' : 'closed'}`}>
            {hp.dewpoint_alarm ? 'ACTIVE' : 'none'}
          </span>
        </div>
        <div className="group-row">
          <span>WPM fault bitmap</span>
          <span className="tabular">{hp.fault_bitmap}</span>
        </div>
      </div>

      <h2>Flow temperature</h2>
      <div className="card">
        <div className="card-header">
          <RangeSelector value={flowRange.rangeKey} onChange={flowRange.setRangeKey} />
        </div>
        <FilterableHistoryChart
          width={680}
          height={200}
          series={[
            { data: flow, label: 'flow temp', filterable: true, average: true },
            { data: outsideHp, label: 'outside temp', filterable: true, average: true },
          ]}
          yLabel="°C"
          {...bucketProps(history.data)}
        />
      </div>

      <h2>Pump &amp; compressor</h2>
      <div className="card">
        <div className="card-header">
          <RangeSelector value={pumpRange.rangeKey} onChange={pumpRange.setRangeKey} />
        </div>
        <HistoryChart
          width={680}
          height={140}
          series={[
            { data: pumpingSeries, label: 'slab pump' },
            { data: compressorSeries, label: 'compressor' },
          ]}
          yLabel={pumpBucketed ? 'duty' : 'on/off'}
        />
      </div>

      <h2>Weather</h2>
      <div className="card">
        <div className="card-header">
          <RangeSelector value={weatherRange.rangeKey} onChange={weatherRange.setRangeKey} />
        </div>
        {/* Same combo as the Trends weather card: solar stays a line (left
            axis) and outside temperature is bars (right axis, measured from
            the domain minimum rather than zero) coloured by cloud cover.
            Weather only ever lands one point per day here too, so a bar per
            day reads the same way it does on Trends. No whisker, though —
            this is a plain history bucket (a per-bucket median), not a
            rollup with separate min/max fields to draw one from. */}
        <FilterableHistoryChart
          width={680}
          height={200}
          series={[{ data: solar, label: 'solar' }]}
          bars={[{
            data: outsideTemp,
            label: 'outside °C',
            axis: 'right',
            fromZero: false,
            colorBy: cloud,
            colorByLabel: 'cloud cover',
          }]}
          yLabel="W/m²"
          yLabelRight="°C"
          {...bucketProps(weather.data)}
        />
      </div>
    </>
  );
}
